/**
 * AnimatedBackground - Background minimalista com configurações dinâmicas
 *
 * Design centralizado em duas cores: preto/cinza escuro e roxo escuro
 * Efeito sutil e elegante com nebulosas roxas + ParticleNetwork
 * Configurável via ThemeContext (BackgroundSettings)
 */

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { ParticleNetwork } from './particle-network';
import { useTheme } from '@/features/theme/contexts/ThemeContext';

interface AnimatedBackgroundProps {
  className?: string;
}

// Utility function to convert hex to rgba
function hexToRgba(hex: string, alpha: number): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return `rgba(88, 28, 135, ${alpha})`;
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Generate nebula gradients based on colors, intensity, and size
function getNebulaStyle(
  color: string,
  intensity: number,
  baseOpacity: number,
  sizeMultiplier: number = 1
): string {
  const adjustedOpacity = (baseOpacity * intensity) / 100;
  const fadeOpacity = adjustedOpacity * 0.3;
  // Size multiplier affects the gradient spread
  const innerStop = Math.min(10 * sizeMultiplier, 40);
  const fadeStop = Math.min(50 * sizeMultiplier, 70);
  return `radial-gradient(circle at center, ${hexToRgba(color, adjustedOpacity)} 0%, ${hexToRgba(color, fadeOpacity)} ${fadeStop}%, transparent ${fadeStop + 20}%)`;
}

// Calculate animation duration based on speed multiplier
function getAnimationDuration(baseDuration: number, speedMultiplier: number): string {
  const duration = baseDuration / speedMultiplier;
  return `${duration}s`;
}

export function AnimatedBackground({ className }: AnimatedBackgroundProps) {
  const { background } = useTheme();

  // Memoize particle config to avoid re-creating on every render
  const particleConfig = useMemo(() => ({
    particleCount: background.particleCount,
    connectRadius: background.lineRange,
    particleSpeed: background.pauseAnimations ? 0 : background.particleSpeed,
    particleRadius: background.particleSize,
    colors: {
      bg: 'transparent',
      particle: hexToRgba(background.particleColor, background.particleOpacity / 100),
      line: hexToRgba(background.particleColor, background.lineOpacity / 100),
    },
    pauseWhenHidden: true,
    adaptive: true,
    debug: false,
  }), [
    background.particleCount,
    background.particleSpeed,
    background.particleColor,
    background.particleOpacity,
    background.particleSize,
    background.lineOpacity,
    background.lineRange,
    background.pauseAnimations,
  ]);

  // Calculate nebula size based on setting (0.5x to 2x)
  const nebulaSizeMultiplier = background.nebulaSize / 100;

  // Calculate blur based on setting
  const nebulaBlurValue = background.nebulaBlur;

  // Memoize nebula styles
  const nebulaStyles = useMemo(() => ({
    nebula1: getNebulaStyle(background.nebulaColor1, background.nebulaIntensity, 0.25, nebulaSizeMultiplier),
    nebula2: getNebulaStyle(background.nebulaColor2, background.nebulaIntensity, 0.20, nebulaSizeMultiplier),
    nebula3: getNebulaStyle(background.nebulaColor1, background.nebulaIntensity, 0.20, nebulaSizeMultiplier),
    nebula4: getNebulaStyle(background.nebulaColor2, background.nebulaIntensity, 0.18, nebulaSizeMultiplier),
    anim1: background.pauseAnimations ? '0s' : getAnimationDuration(25, background.animationSpeed),
    anim2: background.pauseAnimations ? '0s' : getAnimationDuration(30, background.animationSpeed),
    anim3: background.pauseAnimations ? '0s' : getAnimationDuration(28, background.animationSpeed),
    anim4: background.pauseAnimations ? '0s' : getAnimationDuration(22, background.animationSpeed),
    noiseOpacity: background.noiseOpacity / 100,
    blur1: `blur(${nebulaBlurValue}px)`,
    blur2: `blur(${nebulaBlurValue}px)`,
    blur3: `blur(${Math.max(nebulaBlurValue - 10, 50)}px)`,
    blur4: `blur(${Math.max(nebulaBlurValue - 15, 45)}px)`,
    // Nebula dimensions based on size setting
    size1: `${50 * nebulaSizeMultiplier}%`,
    size2: `${45 * nebulaSizeMultiplier}%`,
    size3: `${40 * nebulaSizeMultiplier}%`,
    size4: `${35 * nebulaSizeMultiplier}%`,
  }), [
    background.nebulaColor1,
    background.nebulaColor2,
    background.nebulaIntensity,
    background.animationSpeed,
    background.noiseOpacity,
    background.pauseAnimations,
    nebulaSizeMultiplier,
    nebulaBlurValue,
  ]);

  return (
    <>
      {/* Camada 1: Background base + nebulosas (atrás de tudo) */}
      <div
        className={cn('fixed inset-0 overflow-hidden pointer-events-none', className)}
        style={{ zIndex: -10 }}
      >
        {/* Base - cor configurável */}
        <div
          className="absolute inset-0"
          style={{ backgroundColor: background.baseColor }}
        />

        {/* Nebulosas animadas - cores configuráveis */}
        {background.enableNebulas && (
          <div className="absolute inset-0">
            {/* Nebulosa 1 - canto superior esquerdo */}
            <div
              className="absolute rounded-full"
              style={{
                top: '-15%',
                left: '-15%',
                width: nebulaStyles.size1,
                height: nebulaStyles.size1,
                background: nebulaStyles.nebula1,
                filter: nebulaStyles.blur1,
                animation: background.pauseAnimations
                  ? 'none'
                  : `blob ${nebulaStyles.anim1} ease-in-out infinite`,
              }}
            />

            {/* Nebulosa 2 - canto superior direito */}
            <div
              className="absolute rounded-full"
              style={{
                top: '-10%',
                right: '-10%',
                width: nebulaStyles.size2,
                height: nebulaStyles.size2,
                background: nebulaStyles.nebula2,
                filter: nebulaStyles.blur2,
                animation: background.pauseAnimations
                  ? 'none'
                  : `blob-reverse ${nebulaStyles.anim2} ease-in-out infinite -5s`,
              }}
            />

            {/* Nebulosa 3 - canto inferior esquerdo */}
            <div
              className="absolute rounded-full"
              style={{
                bottom: '-15%',
                left: '-10%',
                width: nebulaStyles.size3,
                height: nebulaStyles.size3,
                background: nebulaStyles.nebula3,
                filter: nebulaStyles.blur3,
                animation: background.pauseAnimations
                  ? 'none'
                  : `blob ${nebulaStyles.anim3} ease-in-out infinite -8s`,
              }}
            />

            {/* Nebulosa 4 - canto inferior direito */}
            <div
              className="absolute rounded-full"
              style={{
                bottom: '-10%',
                right: '-5%',
                width: nebulaStyles.size4,
                height: nebulaStyles.size4,
                background: nebulaStyles.nebula4,
                filter: nebulaStyles.blur4,
                animation: background.pauseAnimations
                  ? 'none'
                  : `blob-reverse ${nebulaStyles.anim4} ease-in-out infinite -10s`,
              }}
            />
          </div>
        )}

        {/* Textura de ruído sutil */}
        {nebulaStyles.noiseOpacity > 0 && (
          <div
            className="absolute inset-0"
            style={{
              opacity: nebulaStyles.noiseOpacity,
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
            }}
          />
        )}
      </div>

      {/* Camada 2: Particle Network - partículas configuráveis */}
      {/* zIndex: -5 para ficar atrás do conteúdo */}
      {background.enableParticles && (
        <div
          className="fixed inset-0 overflow-hidden pointer-events-none"
          style={{ zIndex: -5 }}
        >
          <ParticleNetwork
            className="absolute inset-0"
            config={particleConfig}
          />
        </div>
      )}
    </>
  );
}
